import { editPointOfInterest } from '@/components/Analysis/modules/pointOfInterest/state';
import { allPointOfInterestTemplates } from '@/components/Analysis/state';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { PointOfInterestDocument, PointOfInterestTemplateDocument } from '@/types';
import { useCallback, useMemo, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { AllPointOfInterestTemplate } from '@/types';

export const usePoiFlow = () => {
  const setEditPointOfInterest = useSetAtom(editPointOfInterest);
  const structureId = useGetStructureId();
  const [currentTemplate, setCurrentTemplate] = useState<string>('OTHER');
  const [selectedAssemblyId, setSelectedAssemblyId] = useState<string>('');

  const allTemplates = useAtomValue<AllPointOfInterestTemplate>(allPointOfInterestTemplates);

  const defaultPoi = useMemo(() => {
    return {
      type: 'OTHER',
      name: '',
      assembly: { id: selectedAssemblyId, structure: { id: structureId } },
    };
  }, [selectedAssemblyId, structureId]);

  const changePoiTemplate = useCallback(
    (type: string) => {
      const template = allTemplates.find(({ name }) =>
        name.toUpperCase().includes(type.toUpperCase())
      ) as PointOfInterestTemplateDocument;
      const poiType = template?.key ?? template?.name;
      setCurrentTemplate(type);
      setEditPointOfInterest((current) => ({
        ...current,
        template,
        pointOfInterest: {
          ...defaultPoi,
          type: poiType,
        } as PointOfInterestDocument,
      }));
    },
    [allTemplates, defaultPoi, setEditPointOfInterest]
  );

  const handleCreatePoi = useCallback(() => {
    setEditPointOfInterest((current) => ({
      ...current,
      state: 'WaitingForDetail',
      formState: 'Create',
      pointOfInterest: defaultPoi as PointOfInterestDocument,
      template: allTemplates.find(
        (template) => template.name === 'Other'
      ) as PointOfInterestTemplateDocument,
      isManuallyCreated: true,
    }));
  }, [allTemplates, defaultPoi, setEditPointOfInterest]);

  const setAssemblyId = (assemblyId: string) => {
    setSelectedAssemblyId(assemblyId);
    setEditPointOfInterest((current) => {
      const pointOfInterest = current?.pointOfInterest as PointOfInterestDocument;
      const assembly = { ...pointOfInterest?.assembly } as PointOfInterestDocument['assembly'];
      assembly.id = assemblyId;
      return {
        ...current,
        pointOfInterest: { ...pointOfInterest, assembly },
      };
    });
  };

  return { currentTemplate, selectedAssemblyId, handleCreatePoi, changePoiTemplate, setAssemblyId };
};
